
/**
 * Module dependencies.
 */

import { isNil, size, toUpper } from 'lodash';
import { roundUp } from './math';
import BigNumber from 'bignumber.js';

/**
 * Export `CurrencyOptions` type.
 */

export type CurrencyOptions = {
  assetCode: string;
  decimalPlaces?: number;
  decimalPlacesToDisplay?: number;
  locale: string;
  skipTrailingZeros?: boolean;
  symbol?: string;
};

/**
 * Get fraction digits.
 */

function getFractionDigits(decimalPlacesToDisplay?: number, decimalPlaces?: number): number {
  const maximumDecimalPlaces = 8;
  const minimumFractionDigits = decimalPlacesToDisplay ?? decimalPlaces ?? 0;

  if (isNil(minimumFractionDigits)) {
    return maximumDecimalPlaces;
  }

  return Math.min(minimumFractionDigits, maximumDecimalPlaces);
}

/**
 * Currency formatter.
 */

function currencyFormatter(options: CurrencyOptions): Intl.NumberFormat {
  const { assetCode, decimalPlaces, decimalPlacesToDisplay, locale, skipTrailingZeros } = options;
  const fractionDigits = getFractionDigits(decimalPlacesToDisplay, decimalPlaces);

  return new Intl.NumberFormat(locale, {
    currency: assetCode,
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: skipTrailingZeros ? 0 : fractionDigits,
    style: 'currency'
  });
}

/**
 * Export `formatCurrency`.
 */

export function formatCurrency(value: number | string | BigNumber, options: CurrencyOptions): string {
  const { assetCode, decimalPlaces, locale, symbol } = options;
  const roundedValue = roundUp(value, decimalPlaces);

  try {
    return currencyFormatter(options).format(Number(roundedValue));
  } catch {
    if (typeof Intl === 'undefined') {
      const currencySymbol = symbol ?? toUpper(assetCode);
      const whitespace = size(currencySymbol) > 1 ? ' ' : '';

      return `${currencySymbol}${whitespace}${value}`;
    }

    const formatter = new Intl.NumberFormat(locale);
    const formattedValue = formatter.format(Number(value));

    if (!assetCode) {
      return formattedValue;
    }

    return `${formattedValue}${symbol ?? toUpper(assetCode)}`;
  }
}
